import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, of, takeUntil } from 'rxjs';
import { Prestacion } from 'src/app/models/Prestacion';
import { Especialidad } from 'src/app/models/Especialidad';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ExcelService } from 'src/app/services/excel.service';
import { normalizeText } from 'src/app/shared/text.util';
import type * as XLSXType from 'xlsx-js-style';

@Component({
    selector: 'app-prestaciones',
    templateUrl: './prestaciones.component.html',
    styleUrls: ['./prestaciones.component.css'],
    standalone: false
})
export class PrestacionesComponent implements OnInit {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private _excelService = inject(ExcelService);
  private router = inject(Router);
  private _location = inject(Location);
  
  prestaciones: Prestacion[] = [];
  prestacionesFiltradas$?: Observable<Prestacion[]>;
  prestacionesFiltradasActuales: Prestacion[] = [];
  especialidades: Especialidad[] = [];
  especialidadFiltroTexto = signal<string>('');
  especialidadSeleccionada$ = new BehaviorSubject<number | null>(null);
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  private OnDestroy$ = new Subject();

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession();
        // 2) Datos
        const [prestaciones, especialidades] = await Promise.all([
          firstValueFrom(this._usuarioService.getPrestaciones()),
          firstValueFrom(this._usuarioService.getEspecialidades())
        ]);
        const collator = new Intl.Collator('es', { sensitivity: 'base', ignorePunctuation: true });

        this.prestaciones = prestaciones ?? [];
        this.especialidades = (especialidades ?? [])
          .sort((a, b) => collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? ''));
        this.filterList?.();
      });
    } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this.router.navigate(['/sign-in']);
    }
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.prestacionesFiltradas$ = combineLatest([
      this.searchTerm$,
      this.especialidadSeleccionada$
    ]).pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(([term, idEspecialidad]) => {
        const q = normalizeText(term);

        const filtradas = (this.prestaciones ?? [])
          .filter(p => {
            const coincideTexto = normalizeText(p.x_descripcion).includes(q);

            const coincideEspecialidad =
              !idEspecialidad || Number(p.id_especialidad) === Number(idEspecialidad);

            return coincideTexto && coincideEspecialidad;
          })
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );

        this.prestacionesFiltradasActuales = filtradas;

        return filtradas;
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(prestacion: Prestacion, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!prestacion.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    prestacion.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updatePrestacion(prestacion));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      prestacion.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  async exportarExcel(): Promise<void> {
    const xlsxMod = await import('xlsx-js-style');
    const XLSX = ((xlsxMod as any).default ?? xlsxMod) as any;

    const prestacionesOrdenadas = (
      this.prestacionesFiltradasActuales.length > 0
        ? this.prestacionesFiltradasActuales
        : this.prestaciones
    ).sort((a, b) => {
      const espA = (a.descripcionEspecialidad ?? '').toLowerCase();
      const espB = (b.descripcionEspecialidad ?? '').toLowerCase();

      if (espA !== espB) {
        return espA.localeCompare(espB, 'es');
      }

      return (a.x_descripcion ?? '').localeCompare(
        b.x_descripcion ?? '',
        'es'
      );
    });

    const data = prestacionesOrdenadas.map(p => ({
      Especialidad:
        p.descripcionEspecialidad ??
        this.getEspecialidadDescripcion(p.id_especialidad),

      Prestación: p.x_descripcion ?? '',

      Estado: p.b_activo ? 'Activa' : 'Inactiva',
    }));

    const worksheet: XLSXType.WorkSheet =
      XLSX.utils.json_to_sheet(data);

    // =========================
    // Estilos encabezados
    // =========================
    const headers = ['A1', 'B1', 'C1', 'D1'];

    headers.forEach(cell => {
      if (!worksheet[cell]) return;

      worksheet[cell].s = {
        font: {
          bold: true,
          color: { rgb: 'FFFFFF' },
        },
        fill: {
          fgColor: { rgb: '102E47' },
        },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
        },
      };
    });

    // =========================
    // Ancho columnas
    // =========================
    worksheet['!cols'] = [
      { wch: 28 },
      { wch: 45 },
      { wch: 18 },
      { wch: 14 },
    ];

    // =========================
    // Autofilter
    // =========================
    worksheet['!autofilter'] = {
      ref: `A1:D${data.length + 1}`,
    };

    const workbook: XLSXType.WorkBook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Prestaciones'
    );

    await this._excelService.exportWorkbook(workbook, 'prestaciones.xlsx', XLSX);
  }

  getEspecialidadDescripcion(idEspecialidad?: number): string {
    if (!idEspecialidad) return '';

    return this.especialidades.find(e => Number(e.id) === Number(idEspecialidad))?.x_descripcion ?? '';
  }

  // El combo de especialidades usa un <datalist>: a medida que se escribe, el navegador
  // va filtrando solo las opciones que matchean (orden alfabético porque ya vienen ordenadas).
  onEspecialidadFiltroInput(texto: string): void {
    this.especialidadFiltroTexto.set(texto);

    if (!texto.trim()) {
      this.especialidadSeleccionada$.next(null);
      return;
    }

    const match = this.especialidades.find(e => normalizeText(e.x_descripcion) === normalizeText(texto));
    this.especialidadSeleccionada$.next(match?.id ?? null);
  }

  limpiarFiltroEspecialidad(): void {
    this.especialidadFiltroTexto.set('');
    this.especialidadSeleccionada$.next(null);
  }

  back(){
    this._location.back();
  }
}

